import React from 'react';
import { CameraParams, DEFAULT_CAMERA_PARAMS } from './types';
import { CameraControl3D } from './camera-control';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useI18n } from '@/providers/i18n';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useReactFlow } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { Upload as UploadIcon } from 'lucide-react';

interface CameraPanelProps {
  params: CameraParams;
  onChange: (params: Partial<CameraParams>) => void;
  nodeId?: string;
}

export const CameraPanel = ({ params, onChange, nodeId }: CameraPanelProps) => {
  const { t } = useI18n();
  const { addNodes, addEdges, getNode, setNodes } = useReactFlow();
  const [zoomOpen, setZoomOpen] = React.useState(false);
  const [viewResetNonce, setViewResetNonce] = React.useState(0);
  const QWEN_MODEL_ID = 'wavespeed-ai/qwen-image/edit-2511-lora-multiple-angles';
  const DEFAULT_CONTINUOUS_MODEL_ID = 'nano-banana-2-1k';
  const hasImage =
    typeof (params as any)?.source_image_url === 'string' &&
    String((params as any).source_image_url).trim().length > 0;
  const uploadLabel = hasImage ? t('Click Run in toolbar') : t('Upload Image');
  const continuous = Boolean((params as any)?.continuous);
  const azMin = 0;
  const azMax = continuous ? 360 : 315;
  const azStep = continuous ? 1 : 45;
  // Elevation is stored as "仰角" degrees, where 90° is eye level.
  // Discrete mode uses the classic four steps: 60/90/120/150 (pitch -30/0/30/60).
  const elMin = continuous ? 0 : 60;
  const elMax = continuous ? 360 : 150;
  const elStep = continuous ? 1 : 30;
  const distMin = 0.6;
  const distMax = continuous ? 4 : 1.4;
  const distStep = continuous ? 0.01 : 0.4;

  const handleAddUpload = () => {
    if (!nodeId) return;
    if (hasImage) return;
    try {
      const toolNode = getNode(nodeId) as any;
      if (!toolNode) return;
      const baseX = (toolNode?.position?.x ?? 0) as number;
      const baseY = (toolNode?.position?.y ?? 0) as number;
      const w = (toolNode?.measured?.width ?? 320) as number;
      const gap = 140;
      const newId = nanoid();

      addNodes({
        id: newId,
        type: 'upload',
        position: { x: baseX - w - gap, y: baseY },
        data: {
          fileType: 'image',
          updatedAt: new Date().toISOString(),
        },
        origin: [0, 0.5],
        selected: true,
      } as any);

      try {
        addEdges({
          id: nanoid(),
          source: newId,
          target: nodeId,
          type: 'animated',
        } as any);
      } catch {}

      // Select only the new upload node (so user can immediately upload)
      setTimeout(() => {
        try {
          setNodes((prev: any[]) =>
            prev.map((n: any) => ({ ...n, selected: String(n.id) === String(newId) }))
          );
        } catch {}
      }, 0);
    } catch {}
  };
  
  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-center justify-between px-1">
        <div className="text-sm font-medium">{t('tool.camera.title')}</div>
        <div
          className="flex items-center gap-2"
          onClick={(e) => {
            // Prevent tool node selection / drag interactions.
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="flex items-center gap-2 rounded-md border bg-background/40 px-2 py-1">
            <div className="whitespace-nowrap text-xs text-muted-foreground">
              {t('tool.camera.continuous')}
            </div>
            <Switch
              checked={continuous}
              onCheckedChange={(v) => {
                const next = !!v;
                // Switching modes should reset params to defaults for that mode (and reset view).
                setViewResetNonce((n) => n + 1);

                const model_id = next ? DEFAULT_CONTINUOUS_MODEL_ID : QWEN_MODEL_ID;
                onChange({
                  continuous: next,
                  // reset main params
                  azimuth: DEFAULT_CAMERA_PARAMS.azimuth,
                  elevation: DEFAULT_CAMERA_PARAMS.elevation,
                  distance: DEFAULT_CAMERA_PARAMS.distance,
                  // reset model (continuous mode blocks Qwen)
                  model_id,
                } as any);
              }}
            />
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="h-7"
            onClick={() => {
              setViewResetNonce((n) => n + 1);
              // Reset camera params but keep the "continuous" UI toggle state.
              const next = { ...DEFAULT_CAMERA_PARAMS, continuous } as any;
              if (continuous) next.model_id = DEFAULT_CONTINUOUS_MODEL_ID;
              onChange(next);
            }}
          >
            {t('tool.camera.reset')}
          </Button>
        </div>
      </div>

      <div
        className="w-full h-[220px] bg-neutral-900 rounded-xl overflow-hidden border border-neutral-800 relative cursor-zoom-in"
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setZoomOpen(true);
        }}
        title={t('Double click to zoom') || 'Double click to zoom'}
      >
        <div className="absolute bottom-3 right-3 pointer-events-none z-10">
          <div className="text-xs sm:text-sm text-white/80 drop-shadow-none">
            {t('Double click to zoom')}
          </div>
        </div>
        <CameraControl3D 
          value={params} 
          onChange={onChange} 
          resetViewNonce={viewResetNonce}
        />
      </div>

      <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
        <DialogContent className="sm:max-w-3xl p-4">
          <DialogHeader className="pr-8">
            <DialogTitle>{t('tool.camera.title')}</DialogTitle>
          </DialogHeader>
          <div className="w-full h-[70vh] bg-neutral-900 rounded-xl overflow-hidden border border-neutral-800">
            <CameraControl3D value={params} onChange={onChange} resetViewNonce={viewResetNonce} />
          </div>
        </DialogContent>
      </Dialog>

      <div className="space-y-4 px-1 nodrag">
        {/* Upload / Uploaded (above model selector, same as Relight) */}
        <div className="space-y-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="w-full gap-2 border border-dashed border-primary/70 text-primary hover:bg-primary/10 hover:text-primary"
            disabled={hasImage}
            title={uploadLabel}
            aria-label={uploadLabel}
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddUpload();
            }}
          >
            <UploadIcon size={14} />
            {uploadLabel}
          </Button>
        </div>

        {/* Model selector */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>{t('tool.camera.model')}</Label>
          </div>
          <Select
            value={(() => {
              const cur = (params.model_id ?? QWEN_MODEL_ID) as any;
              // Safety: if continuous is enabled, never keep Qwen selected.
              return continuous && String(cur) === QWEN_MODEL_ID ? (DEFAULT_CONTINUOUS_MODEL_ID as any) : cur;
            })()}
            onValueChange={(v) => onChange({ model_id: v as any } as any)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {continuous ? null : (
                <SelectItem value="wavespeed-ai/qwen-image/edit-2511-lora-multiple-angles">
                  Qwen Multiple Angles (2511)
                </SelectItem>
              )}
              <SelectItem value="nano-banana-2-1k">Nano Banana Pro (1K)</SelectItem>
              <SelectItem value="nano-banana-2-2k">Nano Banana Pro (2K)</SelectItem>
              <SelectItem value="nano-banana-2-4k">Nano Banana Pro (4K)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
            <div className="flex justify-between">
                <Label>{t('tool.camera.azimuth')}</Label>
                <span className="text-xs text-muted-foreground">{Math.round(params.azimuth)}°</span>
            </div>
            <Slider
                min={azMin}
                max={azMax}
                step={azStep}
                value={[params.azimuth]}
                onValueChange={(v) => onChange({ azimuth: v[0] })}
            />
        </div>

        <div className="space-y-2">
            <div className="flex justify-between">
                <Label>{t('tool.camera.elevation')}</Label>
                <span className="text-xs text-muted-foreground">{Math.round(params.elevation)}°</span>
            </div>
            <Slider
                min={elMin}
                max={elMax}
                step={elStep}
                value={[params.elevation]}
                onValueChange={(v) => onChange({ elevation: v[0] })}
            />
        </div>

        <div className="space-y-2">
            <div className="flex justify-between">
                <Label>{t('tool.camera.distance')}</Label>
                <span className="text-xs text-muted-foreground">{params.distance.toFixed(continuous ? 2 : 1)}</span>
            </div>
            <Slider
                min={distMin}
                max={distMax}
                step={distStep}
                value={[params.distance]}
                onValueChange={(v) => onChange({ distance: v[0] })}
            />
        </div>
      </div>
    </div>
  );
};
